import { ReactNode } from "react";
import { Platform } from "react-native";

type TopLayerProps = {
  children: ReactNode;
};

/**
 * Marks its subtree as a react-aria "top layer" so that a surrounding
 * focus-trapping FocusScope (e.g. the one Gluestack's `Modal` installs) will
 * NOT steal focus back from it.
 *
 * Gluestack's `Modal` wraps its content in a react-aria `FocusScope` with
 * `contain`. Any overlay that portals OUT of the modal's DOM subtree — a
 * `Popover`, `Menu`, `Select`, etc. — lives outside that scope, so when you
 * try to focus an input inside it, the modal's `focusin` listener yanks focus
 * straight back and the field never focuses.
 *
 * react-aria's FocusScope has a built-in escape hatch: it treats any element
 * inside a node carrying `data-react-aria-top-layer` as belonging to a child
 * scope and leaves its focus alone. Wrapping the portaled overlay's content in
 * this component adds that attribute (web only — a no-op on native).
 */
export default function TopLayer({ children }: TopLayerProps) {
  if (Platform.OS !== "web") return <>{children}</>;

  // `display: contents` keeps the marker out of the layout tree while staying
  // a real DOM ancestor, so `element.closest('[data-react-aria-top-layer]')`
  // still resolves it.
  return (
    <div data-react-aria-top-layer="true" style={{ display: "contents" }}>
      {children}
    </div>
  );
}
